import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaMobileAlt, FaAndroid, FaApple, FaCode } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { theme } from '../../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary};
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const LocationHighlight = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(1, 191, 113, 0.1);
  }
`;

const CardIcon = styled.div`
  color: #01bf71;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CardText = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const FeaturesList = styled.div`
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FeatureIcon = styled.div`
  color: #01bf71;
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #fff;
`;

const ProcessSection = styled.div`
  margin-bottom: 80px;
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ProcessStep = styled.div`
  display: flex;
  gap: 20px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  background: #01bf71;
  color: #010606;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #01bf71, #018f55);
  border-radius: 10px;
  padding: 60px;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const CTATitle = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: transparent;
  border: 2px solid ${theme.colors.secondary};
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
`;

const AppDevelopmentSouthAfrica = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>App Development in South Africa | Mobile & Web Apps | Afrotech Group</title>
        <meta name="description" content="Professional app development services in South Africa. Custom mobile apps, web applications, and digital solutions for businesses across Africa. Contact Afrotech Group today!" />
        <meta name="keywords" content="app development South Africa, mobile app development, Android apps, iOS apps, web applications, Afrotech Group" />
      </Helmet>
      
      <ContentWrapper>
        <PageHeader>
          <Title>Custom App Development in <LocationHighlight>South Africa</LocationHighlight></Title>
          <Subtitle>
            Building innovative mobile and web applications tailored for South African businesses and their unique market needs
          </Subtitle>
        </PageHeader>
        
        <Section>
          <SectionTitle>Our App Development Services</SectionTitle>
          <Grid>
            <Card>
              <CardIcon><FaAndroid /></CardIcon>
              <CardTitle>Android App Development</CardTitle>
              <CardText>
                Custom Android applications designed for the South African market, with support for local payment gateways and services.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Native Android development</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>South African payment integration</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Offline functionality for rural areas</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaApple /></CardIcon>
              <CardTitle>iOS App Development</CardTitle>
              <CardText>
                Sleek and powerful iOS applications built for South African businesses and consumers with intuitive user experiences.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Swift & SwiftUI development</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Apple Pay integration</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>App Store optimization</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaMobileAlt /></CardIcon>
              <CardTitle>Cross-Platform Apps</CardTitle>
              <CardText>
                Cost-effective solutions that work across multiple platforms, perfect for South African startups and SMEs.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>React Native development</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Flutter applications</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Single codebase efficiency</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaCode /></CardIcon>
              <CardTitle>Progressive Web Apps</CardTitle>
              <CardText>
                Web applications that function like native apps, ideal for South Africa's varying internet connectivity levels.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Offline functionality</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Low data consumption</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Fast loading on all networks</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <ProcessSection>
          <SectionTitle>Our App Development Process</SectionTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepTitle>Discovery & Strategy</StepTitle>
                <StepDescription>
                  We begin by understanding your business goals, target users, and the South African market context. Our team develops a comprehensive app strategy aligned with your objectives.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepTitle>UI/UX Design</StepTitle>
                <StepDescription>
                  Our designers create intuitive and engaging user interfaces tailored to South African users, ensuring your app is both beautiful and functional.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepTitle>Development</StepTitle>
                <StepDescription>
                  Our experienced developers build your app using the latest technologies, following best practices for performance, security, and scalability in the South African context.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepTitle>Testing & Quality Assurance</StepTitle>
                <StepDescription>
                  We rigorously test your app across multiple devices and network conditions common in South Africa to ensure it performs flawlessly for all users.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepTitle>Deployment & Launch</StepTitle>
                <StepDescription>
                  We handle the app store submission process and help you plan a successful launch strategy for the South African market.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>6</StepNumber>
              <StepContent>
                <StepTitle>Maintenance & Updates</StepTitle>
                <StepDescription>
                  Our team provides ongoing support, maintenance, and updates to keep your app running smoothly and evolving with your business needs.
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Build Your Custom App?</CTATitle>
          <CTAText>
            Contact our South African app development team today for a free consultation and quote. Let's create an app that drives your business forward.
          </CTAText>
          <CTAButton to="/contact">Get a Free Consultation</CTAButton>
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AppDevelopmentSouthAfrica;
